import { getOrientationLayout } from "../components/map/shapes/SpotShape/helpers";
import { SpotShape, SpotStateArea } from "../types";
import { getEffectiveSpotAppearance } from "./spots";

interface Point {
  x: number;
  y: number;
}

/** Rotate `point` by `degrees` around `pivot`. */
const rotatePoint = (point: Point, pivot: Point, degrees: number): Point => {
  if (!degrees) return point;
  const rad = (degrees * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = point.x - pivot.x;
  const dy = point.y - pivot.y;
  return {
    x: pivot.x + dx * cos - dy * sin,
    y: pivot.y + dx * sin + dy * cos
  };
};

/**
 * Center of a spot's configured highlight area, in the same spot-local
 * coordinate frame `layout.rect`/`layout.circle` live in — matches the type
 * doc comments on `SpotStateArea.rect`/`.points` ("relative to the spot
 * origin"). Returns null when there's nothing to center on (no rect/points
 * yet, or 'spot-rect' on a spot type that has no rect at all).
 */
const getStateAreaLocalCenter = (
  stateArea: SpotStateArea,
  spot: SpotShape
): Point | null => {
  if (stateArea.kind === "rect") {
    const rect = stateArea.rect;
    if (!rect?.width || !rect.height) return null;
    return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
  }

  if (stateArea.kind === "polygon") {
    const points = stateArea.points;
    if (!points || points.length < 3) return null;
    // Simple vertex average — good enough for centering an icon, no need
    // for a true area-weighted polygon centroid here.
    const sum = points.reduce(
      (acc, point) => ({ x: acc.x + point.x, y: acc.y + point.y }),
      { x: 0, y: 0 }
    );
    return { x: sum.x / points.length, y: sum.y / points.length };
  }

  // 'spot-rect' — the spot's own procedural room/desk/parking rect, resolved
  // the same way SpotShapeItemSVG/SpotShapeItem do for rendering.
  const spotType = getEffectiveSpotAppearance(
    spot.spotType,
    spot.spotAppearance
  );
  const hasRect =
    spotType === "desk" ||
    spotType === "meeting_room" ||
    spotType === "circle" ||
    spotType === "parking_lot" ||
    spotType === "parking_space";
  if (!hasRect) return null;

  const layout = getOrientationLayout(
    spotType,
    spot.orientation,
    spot.labelPlacement,
    spot.labelOrientation || "auto",
    0,
    0,
    spot.spacing || 0,
    spot.rowSpacing || 0,
    // Parking rects are sized from the spot's own configured width/height
    // (e.g. a grid tool cell), not the fixed desk-sized default — without
    // these the computed area center would drift from where the rect
    // actually renders.
    spot.width,
    spot.height
  );
  if (!layout.rect.width || !layout.rect.height) return null;

  // Rotation pivots around the rect's own x,y corner (matches the SVG
  // `rotate(rotation, x, y)` transform used to render it).
  const corner = { x: layout.rect.x, y: layout.rect.y };
  const unrotatedCenter = {
    x: layout.rect.x + layout.rect.width / 2,
    y: layout.rect.y + layout.rect.height / 2
  };
  return rotatePoint(unrotatedCenter, corner, layout.rect.rotation || 0);
};

/**
 * Icon `offsetX`/`offsetY` that centers the state icon on the spot's
 * configured highlight area. Returns null when there's no area configured,
 * or the configured area doesn't currently resolve to any geometry.
 *
 * Accounts for the spot's own `rotation`: the icon is rendered inside a
 * group counter-rotated by `-rotation` (so it stays upright regardless of
 * the spot's orientation — see InteractiveZoneSVG/InteractiveZone), while
 * the highlight area is rendered without that counter-rotation and so
 * fully follows `rotation`. A naive `areaCenter - circle` delta would only
 * be correct for unrotated spots; pre-rotating that delta by `rotation`
 * cancels the counter-rotation out so the icon still lands on the area's
 * true on-screen center.
 */
export const computeStateIconCenterOffset = (
  spot: SpotShape
): { offsetX: number; offsetY: number } | null => {
  if (!spot.stateArea) return null;
  const areaCenter = getStateAreaLocalCenter(spot.stateArea, spot);
  if (!areaCenter) return null;

  const spotType = getEffectiveSpotAppearance(
    spot.spotType,
    spot.spotAppearance
  );
  const layout = getOrientationLayout(
    spotType,
    spot.orientation,
    spot.labelPlacement,
    spot.labelOrientation || "auto",
    0,
    0,
    spot.spacing || 0,
    spot.rowSpacing || 0,
    spot.width,
    spot.height
  );

  const rotated = rotatePoint(areaCenter, layout.circle, spot.rotation || 0);

  return {
    offsetX: rotated.x - layout.circle.x,
    offsetY: rotated.y - layout.circle.y
  };
};

/** Rendering default; explicit Center on Area commands still use the geometric helper. */
export const computeAutomaticStateIconCenterOffset = (spot: SpotShape) =>
  spot.stateArea?.indicatorAnchor === "spot"
    ? null
    : computeStateIconCenterOffset(spot);
